import { createContext, useContext, useMemo } from 'react';

import type {
  UseAccountOptionsFetch,
  UseCreateLookalikeSegment,
  UseLalSegmentFetch,
  UseLalSegmentPermissions,
  UseLalSegmentsListFetch,
  UseLalSegmentsOperation,
  UseLalSegmentsWithDetails,
  UseLalSegmentsWithDetailsFetch,
  UseLalSegmentTableFetch,
  UseProvidersListFetch,
  UseSegmentReachFetch,
  UseSegmentsEstimatedReachFetch,
  UseSegmentsLinkedAudiences,
  UseSegmentsLinkedAudiencesFetch,
  UseSegmentsListFetch,
  UseSegmentsOperation,
  UseSegmentTableFetch,
  UseUpdateLookalikeSegment,
  UseUploadSegment,
} from './SegmentHookTypes.ts';

export interface SegmentProviderConfig {
  isOa?: boolean;
  useAccountOptionsFetch: UseAccountOptionsFetch;
  useSegmentsLinkedAudiences: UseSegmentsLinkedAudiences;
  useSegmentsLinkedAudiencesFetch: UseSegmentsLinkedAudiencesFetch;
  useSegmentsListFetch: UseSegmentsListFetch;
  useSegmentsInfinityScrollListFetch: UseSegmentsListFetch;
  useSegmentsOperation: UseSegmentsOperation;
  useSegmentTableFetch: UseSegmentTableFetch;
  useSegmentReachFetch: UseSegmentReachFetch;
  useUploadSegment: UseUploadSegment;
  useLalSegmentFetch: UseLalSegmentFetch;
  useLalSegmentsOperation: UseLalSegmentsOperation;
  useLalSegmentsWithDetails: UseLalSegmentsWithDetails;
  useLalSegmentsWithDetailsFetch: UseLalSegmentsWithDetailsFetch;
  useProvidersListFetch: UseProvidersListFetch;
  useLalSegmentTableFetch: UseLalSegmentTableFetch;
  useLalSegmentsListFetch: UseLalSegmentsListFetch;
  useCreateLookalikeSegment: UseCreateLookalikeSegment;
  useLalSegmentPermissions: UseLalSegmentPermissions;
  useUpdateLookalikeSegment: UseUpdateLookalikeSegment;
  useSegmentsEstimatedReachFetch: UseSegmentsEstimatedReachFetch;
}

export interface SegmentProviderProps {
  children: React.ReactNode;
  segmentConfig: SegmentProviderConfig;
}

export type SegmentContext = SegmentProviderConfig;

const segmentContext = createContext<SegmentContext>({
  isOa: false,
  useAccountOptionsFetch: () => {
    throw new Error('useAccountOptionsFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  useProvidersListFetch: () => {
    throw new Error('useProvidersListFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  useSegmentReachFetch: () => {
    throw new Error('useSegmentReachFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
  useSegmentTableFetch: () => {
    throw new Error('useSegmentTableFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
  useSegmentsLinkedAudiences: () => {
    throw new Error('useSegmentsLinkedAudiences: Not implemented. Ensure you are inside the SegmentContext');
  },
  useSegmentsLinkedAudiencesFetch: () => {
    throw new Error('useSegmentsLinkedAudiencesFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
  useSegmentsListFetch: () => {
    throw new Error('useSegmentsListFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
  useSegmentsInfinityScrollListFetch: () => {
    throw new Error('useSegmentsInfinityScrollListFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
  useSegmentsOperation: () => {
    throw new Error('useSegmentOperation: Not implemented. Ensure you are inside the SegmentContext');
  },
  useUploadSegment: () => {
    throw new Error('useUploadSegment: Not implemented. Ensure you are inside the SegmentContext');
  },
  useLalSegmentFetch: () => {
    throw new Error('useLalSegmentFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
  useLalSegmentTableFetch: () => {
    throw new Error('useLalSegmentTableFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
  useLalSegmentsListFetch: () => {
    throw new Error('useLalSegmentsListFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
  useLalSegmentsOperation: () => {
    throw new Error('useLalSegmentsOperation: Not implemented. Ensure you are inside the SegmentContext');
  },
  useLalSegmentsWithDetails: () => {
    throw new Error('useLalSegmentsWithDetails: Not implemented. Ensure you are inside the SegmentContext');
  },
  useLalSegmentsWithDetailsFetch: () => {
    throw new Error('useLalSegmentsWithDetailsFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
  useCreateLookalikeSegment: () => {
    throw new Error('useCreateLookalikeSegment: Not implemented. Ensure you are inside the SegmentContext');
  },
  useLalSegmentPermissions: () => {
    throw new Error('useLalSegmentPermissions: Not implemented. Ensure you are inside the SegmentContext');
  },
  useUpdateLookalikeSegment: () => {
    throw new Error('useUpdateLookalikeSegment: Not implemented. Ensure you are inside the SegmentContext');
  },
  useSegmentsEstimatedReachFetch: () => {
    throw new Error('useSegmentsEstimatedReachFetch: Not implemented. Ensure you are inside the SegmentContext');
  },
});

export const useSegmentContext = () => useContext(segmentContext);

export function SegmentProvider({ children, segmentConfig }: SegmentProviderProps) {
  const value: SegmentContext = useMemo(
    () => ({
      ...segmentConfig,
    }),
    [segmentConfig]
  );

  return <segmentContext.Provider value={value}>{children}</segmentContext.Provider>;
}
